import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Printer, Volume2, VolumeX, Bike, Store, Clock,
  ChefHat, Check, CircleCheck, Flame, PauseCircle, Ban, Plus, PackageX, X, Timer,
  AlertTriangle, BarChart3, Moon,
} from 'lucide-react';
import { staffApi } from '../../services/api';
import { printComanda } from '../utils/printComanda';
import EightySixModal from '../components/EightySixModal';
import ShiftStatsModal from '../components/ShiftStatsModal';

const POLL_MS = 10000;
const ACTIVE = ['received', 'confirmed', 'preparing', 'ready'];

const NEXT = {
  received: { to: 'confirmed', label: 'Accetta', Icon: Check },
  confirmed: { to: 'preparing', label: 'Inizia', Icon: ChefHat },
  preparing: { to: 'ready', label: 'Pronto', Icon: CircleCheck },
  ready: { to: 'delivered', label: 'Completa', Icon: Check },
};

const COLUMNS = [
  { key: 'new', title: 'Nuovi', statuses: ['received', 'confirmed'] },
  { key: 'prep', title: 'In preparazione', statuses: ['preparing'] },
  { key: 'ready', title: 'Pronti', statuses: ['ready'] },
];

function beep(times = 1) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    for (let i = 0; i < times; i++) {
      const t0 = ctx.currentTime + i * 0.28;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = times > 1 ? 1040 : 880;
      g.gain.setValueAtTime(0.001, t0);
      g.gain.exponentialRampToValueAtTime(times > 1 ? 0.5 : 0.3, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
      o.start(t0); o.stop(t0 + 0.24);
    }
    setTimeout(() => ctx.close(), times * 300 + 400);
  } catch { /* audio not available */ }
}

function etaStyle(o) {
  if (o.overdue) return { bg: '#7f1d1d', fg: '#fecaca', label: 'In ritardo' };
  const rem = o.minutes_remaining;
  if (rem == null) return null;
  if (rem <= 0) return { bg: '#78350f', fg: '#fde68a', label: 'Pronto ora' };
  if (rem <= 5) return { bg: '#78350f', fg: '#fde68a', label: `~${rem} min` };
  return { bg: '#064e3b', fg: '#a7f3d0', label: `~${rem} min` };
}

const Toggle = ({ on, onClick, label, Icon, testid, activeColor = '#F97316' }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${on ? 'text-white' : 'bg-white/5 text-white/50'}`}
    style={on ? { backgroundColor: activeColor } : {}}>
    <Icon size={15} /> {label}
  </button>
);

export default function StaffKitchen() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kstate, setKstate] = useState(null);
  const [show86, setShow86] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [timeMenuFor, setTimeMenuFor] = useState(null);
  const [sound, setSound] = useState(localStorage.getItem('kds_sound') !== '0');
  const [autoPrint, setAutoPrint] = useState(localStorage.getItem('kds_autoprint') === '1');
  const [printMode, setPrintMode] = useState(localStorage.getItem('kds_printmode') || 'full');
  const seen = useRef(null);
  const overdueSeen = useRef(new Set());
  const autoPrintRef = useRef(autoPrint);
  const soundRef = useRef(sound);
  const printModeRef = useRef(printMode);
  autoPrintRef.current = autoPrint; soundRef.current = sound; printModeRef.current = printMode;

  const loadState = useCallback(async () => {
    try { setKstate((await staffApi.get('/kitchen/state')).data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = (await staffApi.get('/admin/orders')).data;
      const list = (r.orders || []).filter(o => ACTIVE.includes(o.order_status));
      const ids = new Set(list.map(o => o.id));
      if (seen.current === null) {
        seen.current = ids;
      } else {
        const fresh = list.filter(o => !seen.current.has(o.id) && ['received', 'confirmed'].includes(o.order_status));
        if (fresh.length > 0) {
          if (soundRef.current) beep();
          if (autoPrintRef.current) fresh.forEach(o => printComanda(o, printModeRef.current));
        }
        seen.current = ids;
      }
      list.forEach(o => {
        if (o.overdue && !overdueSeen.current.has(o.id)) {
          overdueSeen.current.add(o.id);
          if (soundRef.current) beep(3);
        }
        if (!o.overdue) overdueSeen.current.delete(o.id);
      });
      setOrders(list);
    } catch (e) {
      if (e.response?.status === 401) nav('/staff/login');
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem('staff_token')) { nav('/staff/login'); return; }
    load(); loadState();
    const iv = setInterval(() => { load(); loadState(); }, POLL_MS);
    return () => clearInterval(iv);
  }, [load, loadState, nav]);

  const advance = async (o) => {
    const n = NEXT[o.order_status];
    if (!n) return;
    try {
      await staffApi.put(`/admin/orders/${o.id}/status`, { status: n.to });
      toast.success(`Ordine #${o.order_number}: ${n.label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore aggiornamento stato'); }
  };

  const addTime = async (o, minutes) => {
    setTimeMenuFor(null);
    try {
      await staffApi.post(`/kitchen/orders/${o.id}/add-time`, { minutes });
      toast.success(`+${minutes} min a #${o.order_number}. Cliente avvisato.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const refuse = async (o) => {
    const reason = window.prompt(`Rifiutare l'ordine #${o.order_number}? Motivo (opzionale):`, '');
    if (reason === null) return;
    try {
      const r = (await staffApi.post(`/kitchen/orders/${o.id}/refuse`, { reason })).data;
      toast.success(r.refunded ? `Ordine #${o.order_number} annullato e rimborsato` : `Ordine #${o.order_number} annullato`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const toggleBusy = async () => {
    try { setKstate((await staffApi.post('/kitchen/busy', { on: !kstate?.busy })).data); } catch { toast.error('Errore'); }
  };
  const toggleChannel = async (ch) => {
    const key = ch === 'takeaway' ? 'takeaway_open' : 'delivery_open';
    try { setKstate((await staffApi.post('/kitchen/channels', { [key]: !kstate?.[key] })).data); } catch { toast.error('Errore'); }
  };
  const setPause = async (minutes) => {
    try {
      const r = (await staffApi.post('/kitchen/pause', { minutes })).data;
      setKstate(r);
      toast.success(minutes > 0 ? `Ordini in pausa per ${minutes} min` : 'Ordini riaperti');
    } catch { toast.error('Errore'); }
  };

  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem('kds_sound', v ? '1' : '0'); };
  const toggleAutoPrint = () => { const v = !autoPrint; setAutoPrint(v); localStorage.setItem('kds_autoprint', v ? '1' : '0'); };
  const PRINT_MODES = ['full', 'kitchen', 'counter', 'both'];
  const PRINT_LABELS = { full: 'Completa', kitchen: 'Cucina', counter: 'Banco', both: 'Entrambe' };
  const cyclePrintMode = () => {
    const next = PRINT_MODES[(PRINT_MODES.indexOf(printMode) + 1) % PRINT_MODES.length];
    setPrintMode(next); localStorage.setItem('kds_printmode', next);
  };
  const overdueCount = orders.filter(o => o.overdue).length;

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white" data-testid="staff-kitchen">
      <header className="sticky top-0 z-20 bg-[#18181E] border-b border-white/5 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav('/staff/home')} data-testid="kds-back" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="font-extrabold text-lg leading-none flex items-center gap-2"><ChefHat size={18} className="text-orange-400" /> Cucina</h1>
              <p className="text-[11px] text-white/40 mt-0.5">{orders.length} ordini attivi</p>
            </div>
            {overdueCount > 0 && (
              <span data-testid="kds-overdue-badge" className="ml-2 flex items-center gap-1 text-xs font-extrabold px-2.5 py-1 rounded-full bg-red-600 text-white animate-pulse">
                <AlertTriangle size={13} /> {overdueCount} in ritardo
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowStats(true)} data-testid="kds-stats-open" title="Turno di oggi" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10"><BarChart3 size={16} /></button>
            <button onClick={cyclePrintMode} data-testid="kds-printmode" title="Cosa stampare" className="h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-semibold bg-white/5 text-white/70">
              <Printer size={14} /> {PRINT_LABELS[printMode]}
            </button>
            <button onClick={toggleAutoPrint} data-testid="kds-autoprint-toggle" title="Stampa automatica"
              className={`h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-semibold ${autoPrint ? 'bg-orange-500 text-white' : 'bg-white/5 text-white/60'}`}>
              <Printer size={15} /> Auto
            </button>
            <button onClick={toggleSound} data-testid="kds-sound-toggle" title="Suono"
              className={`w-9 h-9 flex items-center justify-center rounded-full ${sound ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>
              {sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button onClick={() => { load(); loadState(); }} data-testid="kds-refresh" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10"><RefreshCw size={16} /></button>
          </div>
        </div>

        {/* Controls bar */}
        {kstate && (
          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1" data-testid="kds-controls">
            <Toggle on={kstate.busy} onClick={toggleBusy} label={kstate.busy ? `Occupato +${kstate.busy_extra_min}m` : 'Occupato'} Icon={Flame} testid="kds-busy-toggle" activeColor="#DC2626" />
            {kstate.auto_hours && !kstate.within_hours && (
              <span data-testid="kds-outside-hours" className="h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-bold bg-slate-700 text-slate-200 whitespace-nowrap"><Moon size={14} /> Fuori orario</span>
            )}
            <Toggle on={kstate.takeaway_open} onClick={() => toggleChannel('takeaway')} label={kstate.takeaway_open ? 'Ritiro aperto' : 'Ritiro chiuso'} Icon={Store} testid="kds-takeaway-toggle" activeColor="#16A34A" />
            <Toggle on={kstate.delivery_open} onClick={() => toggleChannel('delivery')} label={kstate.delivery_open ? 'Delivery aperto' : 'Delivery chiuso'} Icon={Bike} testid="kds-delivery-toggle" activeColor="#16A34A" />
            {kstate.orders_paused ? (
              <button onClick={() => setPause(0)} data-testid="kds-resume" className="h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-bold bg-amber-500 text-black whitespace-nowrap">
                <PauseCircle size={15} /> In pausa · Riapri
              </button>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-white/40 pl-1">Pausa:</span>
                {[10, 15, 30].map(m => (
                  <button key={m} onClick={() => setPause(m)} data-testid={`kds-pause-${m}`} className="h-9 px-2.5 rounded-full text-xs font-bold bg-white/5 text-white/60 hover:bg-white/10">{m}m</button>
                ))}
              </div>
            )}
            <button onClick={() => setShow86(true)} data-testid="kds-86-open" className="h-9 px-3 flex items-center gap-1.5 rounded-full text-xs font-bold bg-white/5 text-white/70 hover:bg-white/10 whitespace-nowrap ml-auto">
              <PackageX size={15} /> Prodotti 86
            </button>
          </div>
        )}
      </header>

      <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        {COLUMNS.map(col => {
          const colOrders = orders
            .filter(o => col.statuses.includes(o.order_status))
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          return (
            <div key={col.key} data-testid={`kds-col-${col.key}`} className="bg-[#141418] rounded-2xl p-3 min-h-[60vh]">
              <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="font-bold text-sm text-white/80">{col.title}</h2>
                <span className="text-xs font-bold bg-white/10 rounded-full px-2 py-0.5">{colOrders.length}</span>
              </div>
              <div className="space-y-2.5">
                {colOrders.length === 0 && !loading && (
                  <p className="text-center text-white/25 text-sm py-8">Nessun ordine</p>
                )}
                {colOrders.map(o => {
                  const eta = etaStyle(o);
                  const nearDeadline = !o.overdue && o.minutes_remaining != null && o.minutes_remaining <= 5;
                  const isDelivery = o.order_type === 'delivery';
                  const n = NEXT[o.order_status];
                  const NIcon = n?.Icon || Check;
                  return (
                    <div key={o.id} data-testid={`kds-order-${o.id}`}
                      className={`rounded-xl p-3 border ${o.overdue ? 'border-red-500/60 bg-red-950/30 animate-pulse' : nearDeadline ? 'border-amber-500/60 bg-amber-950/20 animate-pulse' : 'border-white/5 bg-[#1C1C22]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-extrabold text-base">#{o.order_number}</span>
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-white/50">
                          {isDelivery ? <Bike size={13} /> : <Store size={13} />}
                          {isDelivery ? 'Delivery' : 'Ritiro'}
                        </span>
                      </div>
                      {eta && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 mb-2" style={{ backgroundColor: eta.bg, color: eta.fg }}>
                          <Clock size={12} /> {eta.label}{o.eta_bump_min ? ' ⏳' : ''}
                        </span>
                      )}
                      <ul className="text-sm space-y-0.5 mb-2">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="flex gap-1.5"><span className="font-bold text-orange-400">{it.quantity}×</span><span className="text-white/85">{it.name}</span></li>
                        ))}
                      </ul>
                      {o.notes && <p className="text-[11px] text-amber-300/80 bg-amber-500/10 rounded px-2 py-1 mb-2">📝 {o.notes}</p>}
                      {o.customer_name && <p className="text-[11px] text-white/40 mb-2">{o.customer_name}</p>}
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => advance(o)} data-testid={`kds-advance-${o.id}`}
                          className="flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white transition-colors">
                          <NIcon size={15} /> {n?.label}
                        </button>
                        <button onClick={() => printComanda(o, printMode)} data-testid={`kds-print-${o.id}`} className="w-10 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20" title="Stampa comanda"><Printer size={15} /></button>
                      </div>
                      <div className="flex gap-2 relative">
                        <button onClick={() => setTimeMenuFor(timeMenuFor === o.id ? null : o.id)} data-testid={`kds-addtime-${o.id}`}
                          className="flex-1 flex items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white/70">
                          <Timer size={13} /> Più tempo
                        </button>
                        <button onClick={() => refuse(o)} data-testid={`kds-refuse-${o.id}`}
                          className="flex-1 flex items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-semibold bg-red-500/10 hover:bg-red-500/20 text-red-300">
                          <Ban size={13} /> Rifiuta
                        </button>
                        {timeMenuFor === o.id && (
                          <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-[#26262E] rounded-lg p-1 shadow-lg z-10">
                            {[5, 10, 15].map(m => (
                              <button key={m} onClick={() => addTime(o, m)} data-testid={`kds-addtime-${o.id}-${m}`} className="px-2.5 py-1 rounded text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white">+{m}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {show86 && <EightySixModal onClose={() => setShow86(false)} />}
      {showStats && <ShiftStatsModal onClose={() => setShowStats(false)} />}
    </div>
  );
}
